import { collection, getDocs } from 'firebase/firestore'
import { db } from '../firebase'

export type CategorySubSectionItem = {
  id: string
  name: string
  nameEn: string
  order: number
  isActive: boolean
}

export type CategoryCatalogItem = {
  id: string
  name: string
  nameEn: string
  order: number
  subSections?: CategorySubSectionItem[]
  /** فلاتر الفئة من Firestore (الحالة / الماركة / القسم…) */
  categoryFilters?: unknown
  /** أسماء قديمة مرتبطة بنفس الفئة (لوحدة تحمي تجاراً قدامى) */
  previousNames?: string[]
}

function parseSubSections(raw: unknown): CategorySubSectionItem[] {
  if (!Array.isArray(raw)) return []
  const list: CategorySubSectionItem[] = []
  raw.forEach((item, index) => {
    if (!item || typeof item !== 'object') return
    const m = item as Record<string, unknown>
    const name = typeof m.name === 'string' ? m.name.trim() : ''
    if (!name) return
    list.push({
      id: typeof m.id === 'string' && m.id.trim() ? m.id.trim() : `sub_${index}`,
      name,
      nameEn: typeof m.nameEn === 'string' ? m.nameEn.trim() : '',
      order: typeof m.order === 'number' ? m.order : index,
      isActive: m.isActive !== false,
    })
  })
  list.sort((a, b) => (a.order !== b.order ? a.order - b.order : a.name.localeCompare(b.name, 'ar')))
  return list
}

let cache: CategoryCatalogItem[] = []
let cachedAt = 0

const CACHE_TTL_MS = 5 * 60 * 1000

function norm(v: string): string {
  return v.trim().toLowerCase()
}

/**
 * تطبيع أسماء الفئات للمقارنة المرنة: يوحّد صور الألف والياء والتاء المربوطة،
 * ويزيل التطويل والمسافات المكرّرة — ليطابق «كمبيوتر ولابتوب» مع «كمبيوتر و لابتوب».
 * لا يُستخدم للعرض، فقط للمطابقة.
 */
export function normalizeCategoryKey(v: string): string {
  return String(v ?? '')
    .replace(/\u0640/g, '') // ـ تطويل
    .replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627') // صور الألف
    .replace(/\u0649/g, '\u064a') // ى ← ي
    .replace(/\u0629/g, '\u0647') // ة ← ه
    .replace(/[\u064B-\u0652\u0670]/g, '') // تشكيل
    .replace(/\s+/g, ' ') // مسافات متكررة
    .trim()
    .toLowerCase()
}

/** كل المفاتيح التي قد تُخزَّن بها الفئة (المعرّف + الأسماء + الأسماء القديمة) */
function categoryAliases(cat: CategoryCatalogItem): string[] {
  const out: string[] = [cat.id, cat.name, cat.nameEn, ...(cat.previousNames ?? [])]
  return out.map((x) => String(x ?? '').trim()).filter(Boolean)
}

/**
 * يحدّد الفئة التي تنتمي إليها قيمة مخزّنة، ويعيد معرّف الفئة (المفتاح المستقر).
 * مراجعة أولى بالنص الحرفي، وثانية بالتطبيع (لتشمل المسافات وصور الحروف).
 */
export function matchCategoryId(value: string, cat: CategoryCatalogItem): string | null {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const exact = norm(raw)
  for (const alias of categoryAliases(cat)) {
    if (norm(alias) === exact) return cat.id
  }
  const soft = normalizeCategoryKey(raw)
  if (!soft) return null
  for (const alias of categoryAliases(cat)) {
    if (normalizeCategoryKey(alias) === soft) return cat.id
  }
  return null
}

/** هل القيمة المخزّنة (معرّف أو اسم قديم) تطابق فئة من الـCatalog؟ */
export function categoryValueMatchesDoc(value: string, cat: CategoryCatalogItem): boolean {
  return matchCategoryId(value, cat) !== null
}

export function listHasCategory(list: string[] | undefined, cat: CategoryCatalogItem): boolean {
  if (!list?.length) return false
  return list.some((v) => categoryValueMatchesDoc(v, cat))
}

export function addCategoryIdToList(list: string[], cat: CategoryCatalogItem): string[] {
  const withoutLegacy = (list ?? []).filter((v) => !categoryValueMatchesDoc(v, cat))
  if (withoutLegacy.includes(cat.id)) return withoutLegacy
  return [...withoutLegacy, cat.id]
}

export function removeCategoryFromList(list: string[], cat: CategoryCatalogItem): string[] {
  return (list ?? []).filter((v) => !categoryValueMatchesDoc(v, cat))
}

export function normalizeCategoryIdList(values: string[], catalog: CategoryCatalogItem[]): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  for (const v of values ?? []) {
    const trimmed = String(v ?? '').trim()
    if (!trimmed) continue
    const match = catalog.find((c) => categoryValueMatchesDoc(trimmed, c))
    const id = (match?.id ?? trimmed).trim()
    if (id && !seen.has(id)) {
      seen.add(id)
      ids.push(id)
    }
  }
  return ids
}

/** بادئة المفاتيح التي لا تقابل فئة موجودة في الإعدادات (أسماء قديمة) */
export const LEGACY_CATEGORY_PREFIX = 'legacy:'

/**
 * يحوّل قيماً مخزّنة (معرّفات أو أسماء قديمة) إلى مفاتيح مستقرة للفلترة:
 * معرّف الفئة إن طابقت الكتالوج، وإلا `legacy:<اسم مطبّع>` حتى لا يختفي
 * أي تاجر من الفلتر بسبب اسم لم يعد موجوداً في الإعدادات.
 */
export function toCategoryKeys(values: string[] | undefined, catalog: CategoryCatalogItem[]): string[] {
  const keys: string[] = []
  const seen = new Set<string>()
  for (const v of values ?? []) {
    const raw = String(v ?? '').trim()
    if (!raw) continue
    const match = catalog.find((c) => matchCategoryId(raw, c))
    const key = match ? match.id : LEGACY_CATEGORY_PREFIX + normalizeCategoryKey(raw)
    if (!seen.has(key)) {
      seen.add(key)
      keys.push(key)
    }
  }
  return keys
}

export async function loadCategoryCatalog(force = false): Promise<CategoryCatalogItem[]> {
  const now = Date.now()
  if (!force && cache.length > 0 && now - cachedAt < CACHE_TTL_MS) {
    return cache
  }

  const snap = await getDocs(collection(db, 'categories'))
  const next: CategoryCatalogItem[] = snap.docs.map((d) => {
    const data = d.data() as any
    const name = String(data?.name ?? data?.nameAr ?? data?.name_ar ?? '').trim()
    const nameEn = String(data?.nameEn ?? data?.name_en ?? '').trim()
    const order = typeof data?.order === 'number' ? data.order : 999999
    const previousNames = Array.isArray(data?.previousNames)
      ? data.previousNames.map((x: unknown) => String(x ?? '').trim()).filter(Boolean)
      : []
    return {
      id: d.id,
      name,
      nameEn,
      order,
      subSections: parseSubSections(data?.subSections),
      categoryFilters: data?.categoryFilters ?? null,
      previousNames,
    }
  })
  next.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order
    return a.name.localeCompare(b.name, 'ar')
  })
  cache = next
  cachedAt = now
  return next
}

export async function resolveCategory(value: string): Promise<CategoryCatalogItem | null> {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const catalog = await loadCategoryCatalog()
  for (const c of catalog) {
    if (matchCategoryId(raw, c)) return c
  }
  return null
}

export async function localizeCategory(value: string, lang: 'ar' | 'en' = 'ar'): Promise<string> {
  const raw = String(value ?? '').trim()
  if (!raw) return raw
  const c = await resolveCategory(raw)
  if (!c) return raw
  if (lang === 'en') return c.nameEn || c.name || raw
  return c.name || c.nameEn || raw
}

export async function localizeCategoryList(values: string[], lang: 'ar' | 'en' = 'ar'): Promise<string[]> {
  const result = await Promise.all(values.map((v) => localizeCategory(v, lang)))
  return result
}
